/**
 * NOCODEBACKEND RUNTIME API SERVICE
 * 
 * NOTE: Due to MCP limitations in the development environment, this service 
 * currently uses a LocalStorage adapter to simulate the NoCodeBackend live database.
 * 
 * Replace the implementations below with your actual NoCodeBackend SDK/API calls 
 * once the `clouds_boarding_house_portal` instance is manually created.
 */

const DB_KEY = 'clouds_boarding_house_db';

const generateId = (prefix) => `${prefix}_${Math.random().toString(36).substr(2, 9)}`;
const delay = (ms = 300) => new Promise(resolve => setTimeout(resolve, ms));

const seedDatabase = () => {
  const db = {
    buildings: [
      { id: 'bld_1', name: 'Cloud Building', description: 'Affordable monthly student bed-space accommodation', floor_count: 2, room_count: 8, monthly_rate: 1500, status: 'active', created_at: new Date().toISOString(), updated_at: new Date().toISOString() },
      { id: 'bld_2', name: 'Rain Building', description: 'Affordable monthly student bed-space accommodation', floor_count: 4, room_count: 11, monthly_rate: 2000, status: 'active', created_at: new Date().toISOString(), updated_at: new Date().toISOString() }
    ],
    floors: [
      { id: 'flr_1', building_id: 'bld_1', floor_name: 'First Floor', floor_number: 1, status: 'active', created_at: new Date().toISOString(), updated_at: new Date().toISOString() },
      { id: 'flr_2', building_id: 'bld_1', floor_name: 'Second Floor', floor_number: 2, status: 'active', created_at: new Date().toISOString(), updated_at: new Date().toISOString() },
      { id: 'flr_3', building_id: 'bld_2', floor_name: 'First Floor', floor_number: 1, status: 'active', created_at: new Date().toISOString(), updated_at: new Date().toISOString() },
      { id: 'flr_4', building_id: 'bld_2', floor_name: 'Second Floor', floor_number: 2, status: 'active', created_at: new Date().toISOString(), updated_at: new Date().toISOString() },
      { id: 'flr_5', building_id: 'bld_2', floor_name: 'Third Floor', floor_number: 3, status: 'active', created_at: new Date().toISOString(), updated_at: new Date().toISOString() },
      { id: 'flr_6', building_id: 'bld_2', floor_name: 'Fourth Floor', floor_number: 4, status: 'active', created_at: new Date().toISOString(), updated_at: new Date().toISOString() }
    ],
    rooms: [
      { id: 'rm_1', building_id: 'bld_1', floor_id: 'flr_1', room_name: 'Room 101', room_number: '101', status: 'active', is_demo: true, created_at: new Date().toISOString(), updated_at: new Date().toISOString() }
    ],
    bed_spaces: [
      { id: 'bed_1', building_id: 'bld_1', floor_id: 'flr_1', room_id: 'rm_1', bed_space_name: 'Bed A', bed_space_number: 'A', monthly_rate: 1500, status: 'available', notes: '', is_demo: true, created_at: new Date().toISOString(), updated_at: new Date().toISOString() },
      { id: 'bed_2', building_id: 'bld_1', floor_id: 'flr_1', room_id: 'rm_1', bed_space_name: 'Bed B', bed_space_number: 'B', monthly_rate: 1500, status: 'occupied', notes: '', is_demo: true, created_at: new Date().toISOString(), updated_at: new Date().toISOString() }
    ]
  };
  localStorage.setItem(DB_KEY, JSON.stringify(db));
  return db;
};

const getDb = () => {
  const data = localStorage.getItem(DB_KEY);
  return data ? JSON.parse(data) : seedDatabase();
};

const saveDb = (db) => localStorage.setItem(DB_KEY, JSON.stringify(db));

// --- API METHODS ---

export const getBuildings = async () => {
  await delay();
  return getDb().buildings;
};

export const getBuildingById = async (id) => {
  await delay();
  const b = getDb().buildings.find(b => b.id === id);
  if (!b) throw new Error('Building not found');
  return b;
};

export const getFloorsByBuilding = async (buildingId) => {
  await delay();
  return getDb().floors.filter(f => f.building_id === buildingId).sort((a, b) => a.floor_number - b.floor_number);
};

export const getFloorById = async (id) => {
  await delay();
  const f = getDb().floors.find(f => f.id === id);
  if (!f) throw new Error('Floor not found');
  return f;
};

export const getRoomsByFloor = async (floorId) => {
  await delay();
  return getDb().rooms.filter(r => r.floor_id === floorId).sort((a, b) => a.room_number.localeCompare(b.room_number));
};

export const getRoomById = async (id) => {
  await delay();
  const r = getDb().rooms.find(r => r.id === id);
  if (!r) throw new Error('Room not found');
  return r;
};

export const getBedSpacesByRoom = async (roomId) => {
  await delay();
  return getDb().bed_spaces.filter(b => b.room_id === roomId).sort((a, b) => a.bed_space_number.localeCompare(b.bed_space_number));
};

export const createRoom = async (data) => {
  await delay();
  const db = getDb();
  const newRoom = {
    id: generateId('rm'),
    ...data,
    status: data.status || 'active',
    is_demo: data.is_demo !== undefined ? data.is_demo : true,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  };
  db.rooms.push(newRoom);
  saveDb(db);
  return newRoom;
};

export const createBedSpace = async (data) => {
  await delay();
  const db = getDb();
  const newBedSpace = {
    id: generateId('bed'),
    ...data,
    status: data.status || 'available',
    is_demo: data.is_demo !== undefined ? data.is_demo : true,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  };
  db.bed_spaces.push(newBedSpace);
  saveDb(db);
  return newBedSpace;
};

export const updateBedSpace = async (id, updates) => {
  await delay();
  const db = getDb();
  const index = db.bed_spaces.findIndex(b => b.id === id);
  if (index === -1) throw new Error('Bed space not found');
  
  db.bed_spaces[index] = {
    ...db.bed_spaces[index],
    ...updates,
    updated_at: new Date().toISOString()
  };
  saveDb(db);
  return db.bed_spaces[index];
};

export const deactivateBedSpace = async (id) => {
  return updateBedSpace(id, { status: 'inactive' });
};